import { Component, Input, Output, EventEmitter, signal, ElementRef, HostListener, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface SelectOption {
  label: string;
  value: any;
}

@Component({
  selector: 'app-ui-select',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UiSelectComponent),
      multi: true
    }
  ],
  template: `
    <div class="relative" (click)="$event.stopPropagation()">
      <!-- Trigger -->
      <div 
        (click)="toggle()"
        class="relative w-full h-11 bg-white border border-gray-300 rounded-lg shadow-sm cursor-pointer
               hover:border-enduro-orange/50 hover:shadow-md transition-all duration-200 
               flex items-center px-4 select-none"
        [class.border-enduro-orange]="isOpen()"
        [class.ring-4]="isOpen()"
        [class.ring-enduro-orange-10]="isOpen()">
        
        <!-- Icon (Optional) -->
        @if (icon) {
            <div class="text-gray-400 mr-3 group-hover:text-enduro-orange transition-colors" [innerHTML]="icon"></div>
        }

        <!-- Label -->
        <div class="flex-grow font-semibold text-sm tracking-wide" 
             [class.text-gray-700]="value" 
             [class.text-gray-400]="!value">
          {{ getLabel() | translate }}
        </div>

        <!-- Chevron -->
        <svg xmlns="http://www.w3.org/2000/svg" 
             class="h-4 w-4 text-gray-300 transition-transform duration-200"
             [class.rotate-180]="isOpen()" 
             fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      <!-- Dropdown Menu -->
      @if (isOpen()) {
        <div class="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-100 py-1 z-50 animate-in fade-in zoom-in-95 duration-100">
          @for (option of options; track option.value) {
            <div 
              (click)="select(option)"
              class="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-orange-50 hover:text-enduro-orange cursor-pointer transition-colors flex items-center justify-between"
              [class.bg-orange-50]="option.value === value"
              [class.text-enduro-orange]="option.value === value">
              {{ option.label | translate }}
              
              @if (option.value === value) {
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                </svg>
              }
            </div>
          }
        </div>
      }
    </div>
  `
})
export class UiSelectComponent implements ControlValueAccessor {
  @Input() value: any = '';
  @Input() placeholder: string = 'Select...';
  @Input() options: SelectOption[] = [];
  @Input() icon: string = ''; // HTML string for icon

  @Output() valueChange = new EventEmitter<any>();

  isOpen = signal(false);

  onChange: any = () => { };
  onTouched: any = () => { };

  constructor(private elementRef: ElementRef) { }

  writeValue(value: any): void {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  toggle() {
    this.isOpen.update(v => !v);
  }

  select(option: SelectOption) {
    this.value = option.value;
    this.valueChange.emit(this.value);
    this.onChange(this.value);
    this.onTouched();
    this.isOpen.set(false);
  }

  getLabel(): string {
    const selected = this.options.find(o => o.value === this.value);
    return selected ? selected.label : this.placeholder;
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isOpen.set(false);
      this.onTouched();
    }
  }
}
